import React, { useState, useEffect, useRef } from 'react';
import './room3.css';
import { useNavigate } from 'react-router-dom';

import Settings from '../components/common/settings';
import Lost from '../components/common/lost';
import Won from '../components/common/won';

import GuestPuzzle from './GuestPuzzle';

import MusicBoxPuzzle from './music';
import MirrorPuzzle from './mirrorpuzzle';
import lockedSound from '../audios/mixkit-select-click-1109.wav';
import settingsSound from '../audios/settoings.wav';

import ballroomBg from '../assets/balll.jpg';

const TOTAL_TIME = 10 * 60;
const ROOM_KEY = 'room3_time';

const Room3 = () => {
  const navigate = useNavigate();
  const [timeLeft, setTimeLeft] = useState(() => {
    localStorage.setItem(ROOM_KEY, Date.now());
    return TOTAL_TIME;
  });

  const [openSettings, setOpenSettings] = useState(false);
  const [showLosePopup, setShowLosePopup] = useState(false);
  const [showWin, setShowWin] = useState(false);

  const [showGuest, setShowGuest] = useState(false);
  const [showMirror, setShowMirror] = useState(false);
  const [showMusicBox, setShowMusicBox] = useState(false);

  const [guestDone, setGuestDone] = useState(false);
  const [mirrorDone, setMirrorDone] = useState(false);
  const [musicBoxDone, setMusicBoxDone] = useState(false);

  const [candleFlicker, setCandleFlicker] = useState(false);

  const lockedAudio = useRef(null);
  const settingsAudio = useRef(null);

  useEffect(() => {
    lockedAudio.current = new Audio(lockedSound);
    settingsAudio.current = new Audio(settingsSound);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      const saved = localStorage.getItem(ROOM_KEY);
      if (!saved) return;
      const elapsed = Math.floor((Date.now() - Number(saved)) / 1000);
      const remaining = Math.max(TOTAL_TIME - elapsed, 0);
      setTimeLeft(remaining);
      if (remaining === 0) {
        setShowLosePopup(true);
        clearInterval(interval);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const t = setInterval(() => {
      setCandleFlicker(true);
      setTimeout(() => setCandleFlicker(false), 600);
    }, 12000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (guestDone && mirrorDone && musicBoxDone) {
      setTimeout(() => setShowWin(true), 800);
    }
  }, [guestDone, mirrorDone, musicBoxDone]);

  const playLockedSound = () => {
    if (!lockedAudio.current) return;
    lockedAudio.current.currentTime = 0;
    lockedAudio.current.play().catch(() => {});
  };

  const playSettingsSound = () => {
    if (!settingsAudio.current) return;
    settingsAudio.current.currentTime = 0;
    settingsAudio.current.play().catch(() => {});
  };

  const minutes = String(Math.floor(timeLeft / 60)).padStart(2, '0');
  const seconds = String(timeLeft % 60).padStart(2, '0');
  const isWarning = timeLeft <= 60;
  const fillPercent = (timeLeft / TOTAL_TIME) * 100;

  return (
    <section
      className={`room3div ${candleFlicker ? 'room3flicker' : ''}`}
      style={{ backgroundImage: `url(${ballroomBg})` }}
    >

      <div className="room3vignette" />
      <div className="room3dust" />

      <div className="room1nav">
        <div />
        <div className="roomtimer">
          <span className="timerlabel">TIME REMAINING</span>
          <div className="timerdiv">
            <span className={`timerdigits ${isWarning ? 'timerwarning' : ''}`}>
              {minutes}:{seconds}
            </span>
          </div>
          <div className="timerbar">
            <div
              className="timerbartill"
              style={{
                width: `${fillPercent}%`,
                background: isWarning ? '#ff4444' : '#D4AF37',
              }}
            />
          </div>
        </div>
        <div className="settings" onClick={() => { playSettingsSound(); setOpenSettings(true); }} />
      </div>

      <div className="room3progress">
        <div className={`room3task ${guestDone ? 'taskdone' : ''}`}>
          {guestDone ? '✓' : '?'} GUEST LIST
        </div>
        <div className={`room3task ${mirrorDone ? 'taskdone' : ''}`}>
          {mirrorDone ? '✓' : '?'} MIRRORS
        </div>
        <div className={`room3task ${musicBoxDone ? 'taskdone' : ''}`}>
          {musicBoxDone ? '✓' : '?'} MUSIC BOX
        </div>
      </div>

      <div
        className={`room3hotspot room3portraits ${guestDone ? 'hotspotsolved' : ''}`}
        onClick={() => { playLockedSound(); setShowGuest(true); }}
        title="Examine the portraits"
      >
        <span>{guestDone ? '✓' : '👁'}</span>
      </div>

      <div
        className={`room3hotspot room3mirrors ${mirrorDone ? 'hotspotsolved' : ''}`}
        onClick={() => { playLockedSound(); setShowMirror(true); }}
        title="Examine the mirrors"
      >
        <span>{mirrorDone ? '✓' : '👁'}</span>
      </div>

      <div
        className={`room3hotspot room3musicbox ${musicBoxDone ? 'hotspotsolved' : ''}`}
        onClick={() => { playLockedSound(); setShowMusicBox(true); }}
        title="Examine the music box"
      >
        <span>{musicBoxDone ? '✓' : '👁'}</span>
      </div>
      {showGuest && (
        <GuestPuzzle
          onClose={() => setShowGuest(false)}
          onSolve={() => { setGuestDone(true); setShowGuest(false); }}
        />
      )}

      {showMirror && (
        <MirrorPuzzle
          onClose={() => setShowMirror(false)}
          onSolve={() => { setMirrorDone(true); setShowMirror(false); }}
        />
      )}

      {showMusicBox && (
        <MusicBoxPuzzle
          onClose={() => setShowMusicBox(false)}
          onSolve={() => { setMusicBoxDone(true); setShowMusicBox(false); }}
          gearFromGuest={guestDone}
          gearFromMirror={mirrorDone}
        />
      )}
      {showWin && (
        <Won
          onReplay={() => {
            localStorage.removeItem(ROOM_KEY);
            window.location.reload();
          }}
          chooseRoomPath="/level4"
        />
      )}

      <Settings
        open={openSettings}
        onClose={() => { playSettingsSound(); setOpenSettings(false); }}
        onPlaySound={playSettingsSound}
        onNavigate={navigate}
      />

      <Lost
        open={showLosePopup}
        onRetry={() => {
          localStorage.removeItem(ROOM_KEY);
          window.location.reload();
        }}
      />
    </section>
  );
};

export default Room3;